import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui';
import { TablePagination } from '@/components/ui/TablePagination';
import { LayoutGrid, Wind, MoreVertical, Zap, Droplet, Lightbulb } from 'lucide-react';
import type { UtilitySystem } from '../types/utility.types';

// ICONS removed

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  operational: { label: 'Hoạt động', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  active:       { label: 'Hoạt động', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  installing:   { label: 'Đang lắp đặt', className: 'bg-blue-50 text-blue-600 border-blue-100' },
  testing:      { label: 'Kiểm tra', className: 'bg-indigo-50 text-indigo-600 border-indigo-100' },
  maintenance_due: { label: 'Cần bảo trì', className: 'bg-orange-50 text-orange-600 border-orange-100' },
  maintenance:  { label: 'Đang bảo trì', className: 'bg-orange-50 text-orange-600 border-orange-100' },
  faulty:       { label: 'Sự cố', className: 'bg-rose-50 text-rose-600 border-rose-100' },
  under_repair: { label: 'Đang sửa', className: 'bg-amber-50 text-amber-600 border-amber-100' },
  inactive:     { label: 'Ngừng hoạt động', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  retired:      { label: 'Thanh lý', className: 'bg-slate-100 text-slate-400 border-slate-200' },
};

interface UtilityTableProps {
  items: UtilitySystem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function UtilityTable({
  items,
  total,
  page,
  pageSize,
  isLoading,
  onPageChange,
  onPageSizeChange,
}: UtilityTableProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-x-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="border-b border-slate-100">
              <th className="px-4 py-3 text-left sm:text-sm text-xs text-slate-500 font-normal">Tên tiện ích</th>
              <th className="px-4 py-3 text-left sm:text-sm text-xs text-slate-500 font-normal">Mã</th>
              <th className="px-4 py-3 text-left sm:text-sm text-xs text-slate-500 font-normal">Loại</th>
              <th className="px-4 py-3 text-left sm:text-sm text-xs text-slate-500 font-normal">Dự án</th>
              <th className="px-4 py-3 text-left sm:text-sm text-xs text-slate-500 font-normal">Khu vực</th>
              <th className="px-4 py-3 text-left sm:text-sm text-xs text-slate-500 font-normal">Trạng thái</th>
              <th className="px-4 py-3 text-center sm:text-sm text-xs text-slate-500 font-normal w-12">...</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-3 bg-slate-100 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-20 text-center">
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center">
                      <LayoutGrid className="w-6 h-6" />
                    </div>
                    <p className="sm:text-sm text-xs">Không có tiện ích nào</p>
                  </div>
                </td>
              </tr>
            ) : items.map((item) => {
              const status = STATUS_MAP[item.status_code] ?? { label: item.status_code, className: 'bg-slate-50 text-slate-500' };
              return (
                <tr key={item.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/utility/detail/${item.id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <img src="/assets/other/img/utility.png" alt="" className="w-6 h-6 rounded-md object-cover shrink-0 border border-slate-100" />
                      <span className="sm:text-sm text-xs text-slate-800">{item.system_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-500">{item.system_code}</td>
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">{item.system_type}</td>
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">{(item.project as any)?.name || '-'}</td>
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">{(item.zone as any)?.name || '-'}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded border sm:text-xs text-[10px] px-2 py-0.5 ${status.className}`}>
                      {status.label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button className="text-slate-400 hover:text-slate-600 p-1 rounded">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-slate-100 shrink-0">
        <TablePagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    </div>
  );
}
